import {
    AsYouType,
    getCountries,
    getCountryCallingCode,
    getExampleNumber,
    parsePhoneNumberFromString,
    type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

// Country list for the phone fields: ISO order (Andorra, UAE, Afghanistan, ...)
// with the English country name and dial code.
export interface CountryOption {
    code: CountryCode;
    dialCode: string;
    name: string;
}

let cached: CountryOption[] | null = null;

export const getCountryOptions = (): CountryOption[] => {
    if (cached) return cached;
    let names: Intl.DisplayNames | null = null;
    try {
        names = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
        names = null;
    }
    cached = getCountries().map((code) => ({
        code,
        dialCode: `+${getCountryCallingCode(code)}`,
        name: names?.of(code) || code,
    }));
    return cached;
};

/** India from the browser's timezone, else the browser's region, else US. */
export const defaultCountry = (): CountryCode => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return "IN";
        const region = (navigator.language || "").split("-")[1]?.toUpperCase();
        if (region && getCountries().includes(region as CountryCode)) return region as CountryCode;
    } catch {
        // fall through
    }
    return "US";
};

/** "+919876543210" -> { country: "IN", national: "9876543210" } */
export const splitPhone = (value?: string | null): { country: CountryCode; national: string } => {
    const fallback = defaultCountry();
    const text = (value || "").trim();
    if (!text) return { country: fallback, national: "" };
    const parsed = parsePhoneNumberFromString(text.startsWith("+") ? text : `+${text}`);
    if (parsed?.country) return { country: parsed.country, national: parsed.nationalNumber };
    return { country: fallback, national: text.replace(/\D/g, "") };
};

/** E.164 for a country + national number, or null when it isn't valid. */
export const toE164 = (country: CountryCode, national: string): string | null => {
    const parsed = parsePhoneNumberFromString(national, country);
    return parsed && parsed.isValid() ? parsed.number : null;
};

/** "(212) 555-7890" while typing. */
export const formatNational = (country: CountryCode, digits: string) => new AsYouType(country).input(digits);

/** "98 **** ****" style placeholder from the country's example mobile number. */
export const maskedExample = (country: CountryCode): string => {
    const example = getExampleNumber(country, examples);
    if (!example) return "Mobile number";
    let shown = 0;
    return example.formatNational().replace(/\d/g, (d) => (shown++ < 2 ? d : "*"));
};

/** "+919876543210" -> "+91 98765 43210" */
export const formatPhone = (value?: string | null): string => {
    if (!value) return "";
    const parsed = parsePhoneNumberFromString(value);
    return parsed ? parsed.formatInternational() : value;
};
